import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { SkeepersAvisVerifiesListingContext } from '../../context/skeepersAvisVerifiesListingContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { renderMarkdown } from '../../util/markdown';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  IconArrowHead,
  CartSidePanel,
  ListingCard,
  VariantSelector,
  TrustBanner,
} from '../../components';
import {
  isApril2026CampaignActive,
  isRoboupLawnMowerListing,
  isUserSignupApril2026,
} from '../../util/promoPrintempsRoboupClient';
import {
  isReolinkSecurityCameraListing,
  persistReolinkPromoUiSession,
  REOLINK_PROMO_CODE,
  shouldShowReolinkPromoPrice,
} from '../../util/promoReolinkWeekendClient';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  fetchSimilarListings,
  fetchBrandListings,
} from './ListingPage.duck';
import {
  addToCart,
  setCartPromoCode,
  addCartPromoCode,
  addBundleCartPromoCode,
} from '../../ducks/cart.duck';
import { buildCartOrderDataForListing } from '../../util/cartOrderDataFromListing';
import { trackRedditViewContentForListing } from '../../util/redditPixelEvents';
import { trackMetaViewContentForListing } from '../../util/metaPixelEvents';
import { pushRecentlyViewedListing } from '../../util/recentlyViewedListings';
import SectionBundles from './SectionBundles';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionDescriptionAndDetails from './SectionDescriptionAndDetails';
import SectionDeliveryInfo from './SectionDeliveryInfo';
import SellerInfoPanel from './SellerInfoPanel';
import LoyaltyProgramPanel from './LoyaltyProgramPanel';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CompatibilityBadges from './CompatibilityBadges';
import SectionConnectivityGrid from './SectionConnectivityGrid';
import ProductBreadcrumb from './ProductBreadcrumb';
import playStoreIcon from '../../assets/play_store_official.png';
import appStoreIcon from '../../assets/app_store_official.png';
import SkeepersCarouselReviews from '../../components/SkeepersCarouselReviews/SkeepersCarouselReviews';
import SkeepersProductReviews from '../../components/SkeepersProductReviews/SkeepersProductReviews';
import { SkeepersAvisVerifiesWidgetMount } from '../../components/SkeepersAvisVerifiesWidget/SkeepersAvisVerifiesWidget';
import css from './ListingPage.module.css';

const YOUTUBE_ID_RE = /^[a-zA-Z0-9_-]{11}$/;

const normalizeSkeepersProductIds = sku => {
  if (sku == null || sku === '') {
    return '';
  }
  if (Array.isArray(sku)) {
    return sku
      .map(value => String(value).trim())
      .filter(Boolean)
      .join(',');
  }
  return String(sku).trim();
};

const ROBOUP_PROMO_CODE = 'PRINTEMPSROBOUP';

const extractYouTubeVideoId = input => {
  if (!input || typeof input !== 'string') return null;
  const str = input.trim();
  if (!str) return null;

  // Accept direct IDs
  if (YOUTUBE_ID_RE.test(str)) return str;

  // Try URL parsing first
  try {
    const url = new URL(str);
    const host = url.hostname.replace(/^www\./, '');

    // youtu.be/<id>
    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0];
      return YOUTUBE_ID_RE.test(id) ? id : null;
    }

    const isYouTubeHost =
      host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com';
    if (!isYouTubeHost) return null;

    // youtube.com/watch?v=<id>
    if (url.pathname === '/watch') {
      const id = url.searchParams.get('v');
      return YOUTUBE_ID_RE.test(id) ? id : null;
    }

    // youtube.com/embed/<id> | /shorts/<id> | /live/<id>
    const parts = url.pathname.split('/').filter(Boolean);
    const marker = parts[0];
    const id = parts[1];
    if (['embed', 'shorts', 'live'].includes(marker) && YOUTUBE_ID_RE.test(id)) {
      return id;
    }
  } catch (e) {
    // fall back to regex matching below
  }

  // Fallback: regex match common patterns
  const match = str.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([a-zA-Z0-9_-]{11})/i
  );
  return match ? match[1] : null;
};

// FAQ Component
const FAQSection = ({ productName, faqs = [] }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqItems = useMemo(
    () => (Array.isArray(faqs) ? faqs.filter(faq => faq && faq.question && faq.answer) : []),
    [faqs]
  );

  useEffect(() => {
    // FAQ items loaded
  }, [productName, faqItems]);

  if (faqItems.length === 0) {
    return null;
  }

  const toggleFaq = index => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className={css.faqSection}>
      <h3 className={css.faqTitle}>Questions fréquemment posées à propos de {productName}</h3>
      <div className={css.faqList}>
        {faqItems.map((faq, index) => (
          <div key={index} className={css.faqItem}>
            <button
              className={css.faqQuestion}
              onClick={() => toggleFaq(index)}
              aria-expanded={openIndex === index}
            >
              <span className={css.faqQuestionText}>{faq.question}</span>
              <IconArrowHead
                direction="down"
                size="small"
                rootClassName={classNames(css.faqIcon, {
                  [css.faqIconOpen]: openIndex === index,
                })}
              />
            </button>
            <div
              className={classNames(css.faqAnswer, {
                [css.faqAnswerHidden]: openIndex !== index,
              })}
            >
              {faq.answer}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Fetch YouTube video metadata (title, author) via noembed oEmbed proxy
const useYouTubeMetadata = videoIds => {
  const [meta, setMeta] = useState({});
  const idsKey = videoIds ? videoIds.join(',') : '';

  useEffect(() => {
    if (!idsKey) return;

    idsKey.split(',').forEach(id => {
      fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${id}`)
        .then(r => r.json())
        .then(data => {
          if (data && data.title) {
            setMeta(prev => ({
              ...prev,
              [id]: { title: data.title, author: data.author_name || '' },
            }));
          }
        })
        .catch(() => {});
    });
  }, [idsKey]);

  return meta;
};

// YouTube Videos Section — Desktop: main player + sidebar thumbnails; Mobile: horizontal carousel
const YouTubeVideosSection = ({ productName, videoIds = [] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const videoMeta = useYouTubeMetadata(videoIds);

  if (!videoIds || videoIds.length === 0) return null;

  const activeId = videoIds[activeIndex] || videoIds[0];

  const updateScrollState = () => {
    const el = carouselRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const scrollCarousel = direction => {
    const el = carouselRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.9;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  return (
    <div className={css.youtubeSection}>
      <h3 className={css.youtubeTitle}>Vidéos à propos de {productName}</h3>

      {/* Desktop layout: main player + sidebar */}
      <div className={css.youtubeDesktopLayout}>
        <div className={css.youtubeMainPlayer}>
          <div className={css.youtubePlayer}>
            <iframe
              key={activeId}
              className={css.youtubeIframe}
              src={`https://www.youtube-nocookie.com/embed/${activeId}?rel=0`}
              title={`Vidéo YouTube — ${productName}`}
              frameBorder="0"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>

        {videoIds.length > 1 && (
          <div className={css.youtubeSidebar}>
            <div className={css.youtubeSidebarHeader}>
              <span className={css.youtubeSidebarCount}>{videoIds.length} vidéos</span>
            </div>
            <div className={css.youtubeSidebarList}>
              {videoIds.map((id, idx) => {
                const m = videoMeta[id] || {};
                return (
                  <button
                    key={id}
                    type="button"
                    className={classNames(css.youtubeSidebarItem, {
                      [css.youtubeSidebarItemActive]: idx === activeIndex,
                    })}
                    onClick={() => setActiveIndex(idx)}
                  >
                    <div className={css.youtubeSidebarThumbWrap}>
                      <img
                        className={css.youtubeSidebarThumb}
                        src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`}
                        alt={m.title || `Vidéo ${idx + 1}`}
                        loading="lazy"
                      />
                      {idx !== activeIndex && (
                        <div className={css.youtubeSidebarPlayIcon}>
                          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      )}
                      {idx === activeIndex && (
                        <div className={css.youtubeSidebarNowPlaying}>
                          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className={css.youtubeSidebarMeta}>
                      <span className={css.youtubeSidebarVideoTitle}>
                        {m.title || `Vidéo ${idx + 1}`}
                      </span>
                      {m.author && <span className={css.youtubeSidebarAuthor}>{m.author}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Mobile layout: horizontal carousel (hidden on desktop) */}
      <div className={css.youtubeMobileCarousel}>
        <div
          className={classNames(css.youtubeCarousel, {
            [css.youtubeCarouselAtEnd]: !canScrollRight,
          })}
        >
          <div ref={carouselRef} className={css.youtubeCarouselTrack} onScroll={updateScrollState}>
            {videoIds.map((id, idx) => (
              <div key={id} className={css.youtubeSlide}>
                <div className={css.youtubePlayer}>
                  <iframe
                    className={css.youtubeIframe}
                    src={`https://www.youtube-nocookie.com/embed/${id}?rel=0`}
                    title={`Vidéo YouTube ${idx + 1} — ${productName}`}
                    frameBorder="0"
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            ))}
          </div>

          {videoIds.length > 1 && (
            <>
              <button
                className={classNames(css.youtubeCarouselArrow, css.youtubeCarouselArrowLeft)}
                onClick={() => scrollCarousel('left')}
                disabled={!canScrollLeft}
                aria-label="Faire défiler les vidéos à gauche"
                type="button"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>
              <button
                className={classNames(css.youtubeCarouselArrow, css.youtubeCarouselArrowRight)}
                onClick={() => scrollCarousel('right')}
                disabled={!canScrollRight}
                aria-label="Faire défiler les vidéos à droite"
                type="button"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Similar Products Section
const SimilarListingsSection = ({ listings, intl, mobileCompareCta }) => {
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [fitsWithoutScroll, setFitsWithoutScroll] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    setFitsWithoutScroll(!hasOverflow);
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [listings, updateScrollState]);

  const scroll = direction => {
    const el = carouselRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.75;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  if (!listings || listings.length === 0) return null;

  return (
    <div className={css.similarProductsSection}>
      <h2 className={css.similarProductsHeading}>Dans la même catégorie</h2>
      {mobileCompareCta}
      <div
        className={classNames(css.similarCarouselContainer, {
          [css.similarCarouselAtEnd]: !canScrollRight,
        })}
      >
        <button
          className={classNames(css.similarCarouselArrow, css.similarCarouselArrowLeft)}
          onClick={() => scroll('left')}
          disabled={!canScrollLeft}
          aria-label="Faire défiler à gauche"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>
        <button
          className={classNames(css.similarCarouselArrow, css.similarCarouselArrowRight)}
          onClick={() => scroll('right')}
          disabled={!canScrollRight}
          aria-label="Faire défiler à droite"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>
        <div
          ref={carouselRef}
          className={classNames(css.similarProductsCarousel, {
            [css.similarProductsCarouselCentered]: fitsWithoutScroll,
          })}
        >
          {listings.map(listing => (
            <div key={listing.id.uuid} className={css.similarProductCard}>
              <ListingCard listing={listing} intl={intl} showBrandInfo />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Brand Products Section
const BrandListingsSection = ({ listings, brandName, intl }) => {
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [fitsWithoutScroll, setFitsWithoutScroll] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    setFitsWithoutScroll(!hasOverflow);
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [listings, updateScrollState]);

  const scroll = direction => {
    const el = carouselRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.75;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  if (!listings || listings.length === 0) return null;

  const heading = brandName ? `Autres produits ${brandName}` : 'Autres produits de la marque';

  return (
    <div className={css.similarProductsSection}>
      <h2 className={css.similarProductsHeading}>{heading}</h2>
      <div
        className={classNames(css.similarCarouselContainer, {
          [css.similarCarouselAtEnd]: !canScrollRight,
        })}
      >
        <button
          className={classNames(css.similarCarouselArrow, css.similarCarouselArrowLeft)}
          onClick={() => scroll('left')}
          disabled={!canScrollLeft}
          aria-label="Faire défiler à gauche"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>
        <button
          className={classNames(css.similarCarouselArrow, css.similarCarouselArrowRight)}
          onClick={() => scroll('right')}
          disabled={!canScrollRight}
          aria-label="Faire défiler à droite"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>
        <div
          ref={carouselRef}
          className={classNames(css.similarProductsCarousel, {
            [css.similarProductsCarouselCentered]: fitsWithoutScroll,
          })}
        >
          {listings.map(listing => (
            <div key={listing.id.uuid} className={css.similarProductCard}>
              <ListingCard listing={listing} intl={intl} showBrandInfo />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const afterSalesIconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

const NeokasaAfterSalesSection = () => {
  const items = [
    {
      title: 'Besoin d’aide pour la mise en route ?',
      text:
        'Notre support dédié vous accompagne pas à pas pour démarrer votre robot en toute sérénité.',
      icon: (
        <svg {...afterSalesIconProps} className={css.afterSalesIcon}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.1 9a3 3 0 015.8 1c0 2-3 2.5-3 4" />
          <path d="M12 17h.01" />
        </svg>
      ),
    },
    {
      title: 'Une pièce défectueuse ?',
      text:
        'Nous disposons d’un stock de pièces détachées et nous vous accompagnons pour la remplacer sereinement.',
      icon: (
        <svg {...afterSalesIconProps} className={css.afterSalesIcon}>
          <path d="M14.7 6.3a4 4 0 00-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 005.4-5.4l-2.4 2.4-3-3 2.4-2.4z" />
        </svg>
      ),
    },
    {
      title: 'Une réparation importante ?',
      text:
        'Le retour est gratuit vers Neokasa ou vers l’un de nos partenaires experts pour une prise en charge SAV adaptée.',
      icon: (
        <svg {...afterSalesIconProps} className={css.afterSalesIcon}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      ),
    },
  ];

  return (
    <section className={css.afterSalesBanner} aria-labelledby="after-sales-heading">
      <div className={css.afterSalesInner}>
        <div className={css.afterSalesHeader}>
          <div className={css.afterSalesTitleRow}>
            <div className={css.afterSalesTitleRowSpacer} aria-hidden="true" />
            <h2 id="after-sales-heading" className={css.afterSalesTitle}>
              Votre robot et le SAV Neokasa
            </h2>
            <div className={css.afterSalesTitleRowEnd}>
              <SkeepersAvisVerifiesWidgetMount
                className={css.afterSalesAvisVerifies}
                ariaLabel="Avis clients vérifiés"
              />
            </div>
          </div>
          <p className={css.afterSalesLead}>
            Après l’achat, notre équipe reste à vos côtés pour diagnostiquer, organiser les retours
            et coordonner les réparations avec les bons interlocuteurs.
          </p>
        </div>
        <ul className={css.afterSalesItems}>
          {items.map(item => (
            <li key={item.title} className={css.afterSalesItem}>
              <span className={css.afterSalesIconBadge}>{item.icon}</span>
              <span className={css.afterSalesItemTitle}>{item.title}</span>
              <span className={css.afterSalesItemText}>{item.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const parseStringifiedStringList = (raw, { splitOnComma = true } = {}) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map(v => (typeof v === 'string' ? v.trim() : String(v || '').trim()))
      .filter(Boolean);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map(v => (typeof v === 'string' ? v.trim() : String(v || '').trim()))
          .filter(Boolean);
      }
    } catch (_) {
      // fall through
    }
    // fallback: newlines (and optionally commas) as separators — warranty prose must not split on commas
    const separator = splitOnComma ? /\r?\n|,/ : /\r?\n/;
    return trimmed
      .split(separator)
      .map(v => v.trim())
      .filter(Boolean);
  }
  // fallback: single value
  const str = String(raw || '').trim();
  return str ? [str] : [];
};

const parseDocumentsList = raw => {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map(doc => ({
        title: doc?.title ? String(doc.title).trim() : '',
        link: doc?.link ? String(doc.link).trim() : '',
      }))
      .filter(doc => doc.title && doc.link);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return parseDocumentsList(parsed);
    } catch (_) {
      return [];
    }
  }
  return [];
};

// Tabs Component
const Tabs = ({ activeTab, onTabChange, publicData, listingTitle = '' }) => {
  const tabs = [
    { id: 'features', label: 'Description' },
    {
      id: 'specs',
      label: (
        <>
          <span className={css.tabLabelDesktop}>Spécifications techniques</span>
          <span className={css.tabLabelMobile}>Fiche technique</span>
        </>
      ),
    },
  ];

  const zigbee2mqttLink = publicData.zigbee_2_mqtt_link;

  const tabContent = {
    features: (
      <div className={css.tabContent}>
        {publicData.extended_description && (
          <>
            <h3>Description détaillée</h3>
            <div className={classNames(css.extendedDescription, css.markdownContent)}>
              {renderMarkdown(publicData.extended_description)}
            </div>
          </>
        )}
        {/* <h3>Caractéristiques principales</h3> */}
        {(() => {
          // Extract features from public data
          const features = [];
          for (let i = 1; i <= 5; i++) {
            const title = publicData[`feature_${i}_title`];
            const text = publicData[`feature_${i}_text`];
            const icon = publicData[`feature_${i}_icon`];

            if (title && text) {
              features.push({
                title,
                text,
                icon: icon || '🔧', // Default icon if none provided
                index: i,
              });
            }
          }

          // If no features found, show a message
          if (features.length === 0) {
            return (
              <div className={css.noFeaturesMessage}>
                <p>Aucune caractéristique produit disponible pour cet article.</p>
              </div>
            );
          }

          // Render features as compact list
          return (
            <div className={css.featuresCompactList}>
              {features.map((feature, index) => (
                <div key={feature.index} className={css.featureCompactItem}>
                  <span className={css.featureIcon}>{feature.icon}</span>
                  <div className={css.featureCompactContent}>
                    <strong>{feature.title}</strong>
                    {/* <span className={css.featureSeparator}> • </span> */}
                    <span>{feature.text}</span>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    ),
    specs: (
      <div className={css.tabContent}>
        <div className={css.specsTableContainer}>
          {(() => {
            const techSpecs = publicData.tech_specs;

            let parsedSpecs = null;
            if (techSpecs) {
              try {
                if (typeof techSpecs === 'object' && techSpecs !== null) {
                  parsedSpecs = techSpecs;
                } else if (typeof techSpecs === 'string') {
                  parsedSpecs = JSON.parse(techSpecs);
                }
              } catch (error) {
                parsedSpecs = null;
              }
            }

            const formatKey = key => {
              return key
                .replace(/_/g, ' ')
                .toLowerCase()
                .replace(/^\w/, l => l.toUpperCase())
                .replace(/\b(wi-fi|wifi)\b/gi, 'Wi-Fi')
                .replace(/\b(usb|hdmi|rj45|ip)\b/gi, l => l.toUpperCase())
                .replace(/\b(ghz|mhz|mb|gb|kb|tb)\b/gi, l => l.toUpperCase())
                .replace(/\b(ieee|wpa|wpa2|wpa3|mqtt|http|https|coap)\b/gi, l => l.toUpperCase());
            };

            const renderSpecRows = (specs, level = 0) => {
              if (level > 4 || !specs || typeof specs !== 'object') return null;

              return Object.entries(specs).map(([key, value]) => {
                const isNested = value && typeof value === 'object' && !Array.isArray(value);
                const displayKey = formatKey(key);

                if (isNested && level < 4) {
                  return (
                    <React.Fragment key={key}>
                      <div className={css.specsGroupHeader}>
                        <span className={css.specsGroupIndicator} />
                        <span className={css.specsGroupTitle}>{displayKey}</span>
                      </div>
                      <div className={css.specsGroupContent}>
                        {renderSpecRows(value, level + 1)}
                      </div>
                    </React.Fragment>
                  );
                }

                return (
                  <div key={key} className={css.specsRow}>
                    <div className={css.specsKey}>{displayKey}</div>
                    <div className={css.specsValue}>
                      {Array.isArray(value) ? value.join(', ') : String(value)}
                    </div>
                  </div>
                );
              });
            };

            const renderSpecsPanel = specs => {
              if (!specs || typeof specs !== 'object') return null;

              const nestedKeys = [];
              const simpleKeys = [];

              Object.entries(specs).forEach(([key, value]) => {
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                  nestedKeys.push({ key, value });
                } else {
                  simpleKeys.push({ key, value });
                }
              });

              return (
                <div className={css.specsPanel}>
                  <div className={css.specsPanelHeader}>
                    <span className={css.specsPanelTitle}>Fiche technique</span>
                  </div>

                  {simpleKeys.length > 0 && (
                    <div className={css.specsSection}>
                      <div className={css.specsSectionHeader}>
                        <h4 className={css.specsSectionTitle}>Général</h4>
                      </div>
                      <div className={css.specsSectionContent}>
                        {simpleKeys.map(({ key, value }) => (
                          <div key={key} className={css.specsRow}>
                            <div className={css.specsKey}>{formatKey(key)}</div>
                            <div className={css.specsValue}>
                              {Array.isArray(value) ? value.join(', ') : String(value)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {nestedKeys.map(({ key, value }) => (
                    <div key={key} className={css.specsSection}>
                      <div className={css.specsSectionHeader}>
                        <h4 className={css.specsSectionTitle}>{formatKey(key)}</h4>
                      </div>
                      <div className={css.specsSectionContent}>{renderSpecRows(value, 1)}</div>
                    </div>
                  ))}
                </div>
              );
            };

            if (
              parsedSpecs &&
              typeof parsedSpecs === 'object' &&
              Object.keys(parsedSpecs).length > 0
            ) {
              return renderSpecsPanel(parsedSpecs);
            }

            return (
              <div className={css.noSpecsMessage}>
                <svg
                  className={css.noSpecsIcon}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                </svg>
                <p>Aucune spécification technique disponible pour ce produit.</p>
              </div>
            );
          })()}
        </div>
      </div>
    ),
  };

  return (
    <div className={css.tabsContainer}>
      <div className={css.tabsHeader}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={classNames(css.tabButton, {
              [css.activeTab]: activeTab === tab.id,
            })}
            onClick={() => {
              onTabChange(tab.id);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className={css.tabBody}>{tabContent[activeTab]}</div>
    </div>
  );
};

const ProductBoxWarrantyDocumentsSection = ({ inTheBox = [], warranty = [], documents = [] }) => {
  const [documentsExpanded, setDocumentsExpanded] = useState(false);

  const hasInTheBox = Array.isArray(inTheBox) && inTheBox.length > 0;
  const hasWarranty = Array.isArray(warranty) && warranty.length > 0;
  const hasDocuments = Array.isArray(documents) && documents.length > 0;
  const shouldCollapseDocuments = documents.length >= 3;

  return (
    <section className={css.smartAdvantageSection}>
      <h2 className={css.smartAdvantageSectionHeading}>Quelques informations en plus</h2>
      <div className={classNames(css.smartAdvantageGrid, css.smartAdvantageGridNoMain)}>
        <aside
          className={classNames(css.smartAdvantageSideColumn, css.smartAdvantageSideColumnOnly, {
            [css.smartAdvantageSideColumnTwo]: !hasInTheBox,
          })}
        >
          {hasInTheBox ? (
            <div className={css.smartAdvantageInfoBlock}>
              <h3 className={css.smartAdvantageInfoTitle}>Contenu de la boîte</h3>
              <ul className={css.productInfoList}>
                {inTheBox.map((item, idx) => (
                  <li key={`${item}-${idx}`} className={css.productInfoListItem}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className={css.smartAdvantageInfoBlock}>
            <h3 className={css.smartAdvantageInfoTitle}>Garanties</h3>
            {hasWarranty ? (
              <ul className={css.productInfoList}>
                {warranty.map((item, idx) => {
                  const label =
                    typeof item === 'string'
                      ? item
                      : item?.label || item?.title || item?.name
                      ? String(item.label || item.title || item.name).trim()
                      : '';
                  const value =
                    typeof item === 'object' && item
                      ? item?.value || item?.description
                        ? String(item.value || item.description).trim()
                        : ''
                      : '';
                  const text = value ? `${label} : ${value}` : label;
                  return text ? (
                    <li key={`${label}-${idx}`} className={css.productInfoListItem}>
                      {text}
                    </li>
                  ) : null;
                })}
              </ul>
            ) : (
              <p className={css.smartAdvantageInfoValue}>Garantie légale de conformité de 2 ans</p>
            )}
          </div>

          <div className={css.smartAdvantageInfoBlock}>
            <h3 className={css.smartAdvantageInfoTitle}>Documents</h3>
            {hasDocuments ? (
              <div className={css.productDocumentsList}>
                {shouldCollapseDocuments && !documentsExpanded ? (
                  <button
                    onClick={() => setDocumentsExpanded(true)}
                    className={classNames(
                      css.homeAssistantIntegrationLink,
                      css.productDocumentButtonCompact
                    )}
                    type="button"
                  >
                    {documents.length} document{documents.length > 1 ? 's' : ''} PDF
                    <svg
                      className={classNames(css.externalLinkIcon, css.downloadIcon)}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    >
                      <path d="M12 4v11" />
                      <path d="M8 11l4 4 4-4" />
                      <path d="M5 19h14" />
                    </svg>
                  </button>
                ) : (
                  documents.map((doc, index) => (
                    <a
                      key={`${doc.link}-${index}`}
                      href={doc.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classNames(
                        css.homeAssistantIntegrationLink,
                        css.productDocumentButtonCompact
                      )}
                    >
                      {doc.title}
                      <svg
                        className={classNames(css.externalLinkIcon, css.downloadIcon)}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      >
                        <path d="M12 4v11" />
                        <path d="M8 11l4 4 4-4" />
                        <path d="M5 19h14" />
                      </svg>
                    </a>
                  ))
                )}
              </div>
            ) : (
              <p className={css.smartAdvantageInfoValue}>Aucun document</p>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
};

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID, Money } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState('features');
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [sidePanelUrl, setSidePanelUrl] = useState('');
  const [iframeLoading, setIframeLoading] = useState(true);
  const [sellerPanelOpen, setSellerPanelOpen] = useState(false);
  const [loyaltyPanelOpen, setLoyaltyPanelOpen] = useState(false);
  const [cartPanelOpen, setCartPanelOpen] = useState(false);
  /** Must stay with top-level hooks — never after loading/error early returns */
  const [addingBundleId, setAddingBundleId] = useState(null);
  /** null = en attente du widget Skeepers ; 'shown' | 'empty' après résolution */
  const [skeepersWidgetStatus, setSkeepersWidgetStatus] = useState(null);

  const handleSkeepersProductResolve = useCallback(hasContent => {
    setSkeepersWidgetStatus(hasContent ? 'shown' : 'empty');
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setSkeepersWidgetStatus(null);
  }, [props.params?.id]);

  // Validate URL is from trusted domains
  const isUrlSafe = url => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      const trustedDomains = [
        'home-assistant.io',
        'www.home-assistant.io',
        'zigbee2mqtt.io',
        'www.zigbee2mqtt.io',
        'github.com',
        'www.github.com',
      ];
      return trustedDomains.some(
        domain => urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain)
      );
    } catch {
      return false;
    }
  };

  const openSidePanel = url => {
    if (isUrlSafe(url)) {
      setSidePanelUrl(url);
      setSidePanelOpen(true);
      setIframeLoading(true);
    } else {
      // Fallback to opening in new tab if URL is not from trusted domain
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const openSellerPanel = () => {
    setSellerPanelOpen(true);
  };

  const closeSellerPanel = () => {
    setSellerPanelOpen(false);
  };

  const openLoyaltyPanel = () => {
    setLoyaltyPanelOpen(true);
  };

  const closeLoyaltyPanel = () => {
    setLoyaltyPanelOpen(false);
  };

  // Handle side panel open/close effects
  useEffect(() => {
    if (sidePanelOpen) {
      // Block body scroll when panel is open
      document.body.style.overflow = 'hidden';

      // Handle Escape key
      const handleEscape = e => {
        if (e.key === 'Escape') {
          setSidePanelOpen(false);
        }
      };
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [sidePanelOpen]);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    showListingInProgress,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    similarListings = [],
    brandListings = [],
    onFetchSimilarListings,
    onFetchBrandListings,
    bundles = [],
    bundleProductListingsById,
    onAddToCart,
    onAddCartPromoCode,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const params = { slug: rawParams.slug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'information';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  const topbar = <TopbarContainer />;

  // Get brand display name from config
  const brandFieldConfig = config.listing.listingFields?.find(field => field.key === 'brand');
  const brandEnumOptions = brandFieldConfig?.enumOptions || [];
  const getBrandDisplayName = brandValue => {
    if (!brandValue) return null;
    // Handle array case (brand can be an array)
    const brandStr = Array.isArray(brandValue) ? brandValue[0] : brandValue;
    if (typeof brandStr !== 'string') return brandStr;
    const brandOption = brandEnumOptions.find(option => option.option === brandStr);
    if (brandOption) return brandOption.label;
    // Deslugify brand: replace hyphens with spaces and capitalize words
    return brandStr
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to get pictogram for connectivity/compatibility items
  const getPictogram = item => {
    const pictograms = {
      wifi: '📶',
      bluetooth: '📡',
      zigbee: '🔗',
      'z-wave': '🌊',
      thread: '🧵',
      matter: '⚡',
      homekit: '🏠',
      alexa: '🔊',
      google: '🎤',
      siri: '💬',
      smartthings: '📱',
      homeassistant: '🏠',
      homey: '🌈',
      ifttt: '🔗',
      webhook: '🌐',
      mqtt: '📨',
      http: '🌍',
      coap: '📦',
      lora: '📡',
      cellular: '📞',
      ethernet: '🔌',
    };
    return pictograms[item.toLowerCase()] || '🔧';
  };

  // Helper function to get display value from config
  const getDisplayValue = (fieldKey, value) => {
    const fieldConfig = config.listing.listingFields?.find(field => field.key === fieldKey);
    if (!fieldConfig || !fieldConfig.enumOptions) return value;

    if (Array.isArray(value)) {
      return value
        .map(v => {
          const option = fieldConfig.enumOptions.find(opt => opt.option === v);
          return option ? option.label : v;
        })
        .join(', ');
    } else {
      const option = fieldConfig.enumOptions.find(opt => opt.option === value);
      return option ? option.label : value;
    }
  };

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { askedQuestionsRaw, askedQuestions, askedQuestionsError } = useMemo(() => {
    const raw = currentListing?.attributes?.publicData?.asked_questions;

    if (raw === undefined) {
      return { askedQuestionsRaw: raw, askedQuestions: [], askedQuestionsError: null };
    }

    if (Array.isArray(raw)) {
      const filtered = raw.filter(faq => faq && faq.question && faq.answer);
      return { askedQuestionsRaw: raw, askedQuestions: filtered, askedQuestionsError: null };
    }

    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const filtered = parsed.filter(faq => faq && faq.question && faq.answer);
          return { askedQuestionsRaw: parsed, askedQuestions: filtered, askedQuestionsError: null };
        }
        return {
          askedQuestionsRaw: raw,
          askedQuestions: [],
          askedQuestionsError: new Error('Parsed asked_questions string is not an array'),
        };
      } catch (e) {
        return {
          askedQuestionsRaw: raw,
          askedQuestions: [],
          askedQuestionsError: e,
        };
      }
    }

    return {
      askedQuestionsRaw: raw,
      askedQuestions: [],
      askedQuestionsError: new Error('asked_questions has unsupported type'),
    };
  }, [currentListing]);

  const { youtubeLinksRaw, youtubeVideoIds, youtubeLinksError } = useMemo(() => {
    const raw = currentListing?.attributes?.publicData?.youtube_links;

    if (raw === undefined || raw === null || raw === '') {
      return { youtubeLinksRaw: raw, youtubeVideoIds: [], youtubeLinksError: null };
    }

    const normalizeToArray = value => {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          // If parsing fails, treat the string as a single URL/id
          return [value];
        }
      }
      return [];
    };

    try {
      const arr = normalizeToArray(raw)
        .map(v => (typeof v === 'string' ? v.trim() : ''))
        .filter(Boolean);

      const ids = Array.from(new Set(arr.map(extractYouTubeVideoId).filter(Boolean)));
      return { youtubeLinksRaw: raw, youtubeVideoIds: ids, youtubeLinksError: null };
    } catch (e) {
      return { youtubeLinksRaw: raw, youtubeVideoIds: [], youtubeLinksError: e };
    }
  }, [currentListing]);

  useEffect(() => {
    const listingId = currentListing?.id?.uuid;

    if (askedQuestionsRaw === undefined) {
      return;
    }

    if (askedQuestionsError) {
      return;
    }

    // asked_questions loaded
  }, [askedQuestionsRaw, askedQuestions, askedQuestionsError, currentListing]);

  useEffect(() => {
    if (youtubeLinksRaw === undefined) return;
    if (youtubeLinksError) return;
    // youtube_links loaded
  }, [youtubeLinksRaw, youtubeVideoIds, youtubeLinksError]);

  // Effects that depend on listing data must be declared before any early returns
  // to keep hook order stable between loading & loaded renders.
  const listingUuidMaybe = currentListing?.id?.uuid;
  const categoryLevel1Maybe = currentListing?.attributes?.publicData?.categoryLevel1;
  const brandMaybe = currentListing?.attributes?.publicData?.brand;
  const brandKeyMaybe = Array.isArray(brandMaybe) ? brandMaybe[0] : brandMaybe;

  useEffect(() => {
    if (listingUuidMaybe && categoryLevel1Maybe && onFetchSimilarListings) {
      onFetchSimilarListings(currentListing.id, categoryLevel1Maybe, config);
    }
  }, [listingUuidMaybe, categoryLevel1Maybe]); // eslint-disable-line

  useEffect(() => {
    if (listingUuidMaybe && brandKeyMaybe && onFetchBrandListings) {
      onFetchBrandListings(currentListing.id, brandMaybe, config);
    }
  }, [listingUuidMaybe, brandKeyMaybe]); // eslint-disable-line

  useEffect(() => {
    if (!listingUuidMaybe || !currentListing?.id) {
      return;
    }
    trackRedditViewContentForListing(currentListing);
    trackMetaViewContentForListing(currentListing);
    pushRecentlyViewedListing(currentListing);
  }, [listingUuidMaybe]);

  useEffect(() => {
    if (new URLSearchParams(location.search || '').get('promoReolink') === '1') {
      persistReolinkPromoUiSession();
    }
  }, [location.search]);

  const hasPromoSignupFlag = new URLSearchParams(location.search || '').get('promoRoboup') === '1';
  const hasPromoReolinkFlag =
    new URLSearchParams(location.search || '').get('promoReolink') === '1';
  const publicDataForPromo = currentListing?.attributes?.publicData;
  const isRoboupPromoEligibleAfterSignup =
    hasPromoSignupFlag &&
    !!currentUser?.id &&
    isApril2026CampaignActive() &&
    isUserSignupApril2026(currentUser?.attributes?.createdAt) &&
    isRoboupLawnMowerListing(publicDataForPromo || {});

  const reolinkPromoConsumed =
    currentUser?.attributes?.privateData?.promos?.reolinkWeekend2026Consumed === true;
  const isReolinkPromoEligibleAfterSignup =
    shouldShowReolinkPromoPrice({
      currentUser,
      hasPromoQueryFlag: hasPromoReolinkFlag,
      reolinkPromoConsumed,
    }) && isReolinkSecurityCameraListing(publicDataForPromo || {});

  const showPromoPriceOnListing =
    isRoboupPromoEligibleAfterSignup || isReolinkPromoEligibleAfterSignup;

  const listingForOrderPanel = useMemo(() => {
    if (!showPromoPriceOnListing) return currentListing;
    const currentPrice = currentListing?.attributes?.price;
    const amount = currentPrice?.amount;
    if (!Number.isFinite(amount) || amount <= 0) return currentListing;

    const currency = currentPrice?.currency;
    if (!currency) return currentListing;
    const discountedAmount = Math.max(0, Math.round(amount * 0.95));
    if (discountedAmount === amount) return currentListing;

    return {
      ...currentListing,
      attributes: {
        ...currentListing.attributes,
        price: new Money(discountedAmount, currency),
      },
    };
  }, [currentListing, showPromoPriceOnListing]);

  const campaignPromoStrikeThroughPrice = useMemo(() => {
    if (!showPromoPriceOnListing) return null;
    const p = currentListing?.attributes?.price;
    const amount = p?.amount;
    if (!Number.isFinite(amount) || amount <= 0) return null;
    const discountedAmount = Math.max(0, Math.round(amount * 0.95));
    if (discountedAmount === amount) return null;
    return p;
  }, [showPromoPriceOnListing, currentListing]);

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  // 404 errors are terminal: show NotFound regardless of any in-flight retry
  if (showListingError && showListingError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  // Prefer the loading spinner over a red error flash whenever a fetch is in
  // flight or the cached listing is not yet fully hydrated (e.g. partial data
  // coming from a previous search/similar-listings query).
  const hasFullListingData =
    currentListing.id &&
    currentListing.attributes &&
    currentListing.attributes.publicData &&
    currentListing.attributes.publicData.listingType &&
    currentListing.attributes.publicData.transactionProcessAlias &&
    currentListing.attributes.publicData.unitType;

  if (showListingInProgress && !hasFullListingData) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  if (showListingError) {
    // Other error in fetching listing (fetch is no longer in progress)
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // No cached listing and no fetch in flight yet (e.g. before loadData runs)
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }
  // Check if listing attributes are fully loaded before extracting publicData
  if (!currentListing.attributes || !currentListing.attributes.publicData) {
    // Still loading listing attributes
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  // Update params with slug if not already set
  if (!params.slug) {
    params.slug = createSlug(title || '');
  }
  const listingSlug = params.slug;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const categoryLevel1 = publicData.categoryLevel1;
  const shortDescription = publicData.shortdescription;
  const brand = publicData.brand;
  const worksWithHomeAssistant = publicData.works_with_home_assistant;
  const connectivity = publicData.connectivity;
  const compatibility = publicData.compatibility;
  const homeAssistantIntegrationLink = publicData.home_assistant_integration_link;
  const zigbee2mqttLink = publicData.zigbee_2_mqtt_link;
  const hasCompatibilityData = Array.isArray(compatibility)
    ? compatibility.length > 0
    : typeof compatibility === 'string'
    ? compatibility.trim().length > 0
    : !!compatibility;
  const hasCompatibilityBlockContent =
    hasCompatibilityData || Boolean(homeAssistantIntegrationLink) || Boolean(zigbee2mqttLink);
  const smartAdvantageRaw = publicData.smart_advantage;
  const smartAdvantageData =
    smartAdvantageRaw && typeof smartAdvantageRaw === 'object' && !Array.isArray(smartAdvantageRaw)
      ? smartAdvantageRaw
      : typeof smartAdvantageRaw === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(smartAdvantageRaw);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
          } catch (e) {
            return {};
          }
        })()
      : {};
  const smartAdvantageTitle =
    typeof smartAdvantageData.title === 'string' && smartAdvantageData.title.trim()
      ? smartAdvantageData.title.trim()
      : '';

  const smartAdvantageUseCasesRaw = smartAdvantageData.use_cases;
  const smartAdvantageUseCasesParsed =
    typeof smartAdvantageUseCasesRaw === 'string'
      ? (() => {
          const trimmed = smartAdvantageUseCasesRaw.trim();
          if (!trimmed) {
            return [];
          }
          try {
            const parsed = JSON.parse(trimmed);
            return Array.isArray(parsed) ? parsed : [trimmed];
          } catch (e) {
            return trimmed.split(/\r?\n|,/);
          }
        })()
      : smartAdvantageUseCasesRaw;
  const smartAdvantageUseCases = Array.isArray(smartAdvantageUseCasesParsed)
    ? smartAdvantageUseCasesParsed
        .map(item => (typeof item === 'string' ? item.trim() : `${item || ''}`.trim()))
        .filter(Boolean)
    : [];
  const hasSmartAdvantageContent = !!smartAdvantageTitle || smartAdvantageUseCases.length > 0;
  const connectivityDisplay =
    connectivity && `${getDisplayValue('connectivity', connectivity)}`.trim()
      ? `${getDisplayValue('connectivity', connectivity)}`.trim()
      : 'Non renseigné';
  const mobileAppRaw = publicData.mobile_app;
  const mobileAppData =
    mobileAppRaw && typeof mobileAppRaw === 'object' && !Array.isArray(mobileAppRaw)
      ? mobileAppRaw
      : typeof mobileAppRaw === 'string'
      ? (() => {
          const trimmed = mobileAppRaw.trim();
          if (!trimmed) {
            return null;
          }
          try {
            const parsed = JSON.parse(trimmed);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
          } catch (e) {
            return null;
          }
        })()
      : null;
  const hasMobileAppName = Boolean(
    mobileAppData && typeof mobileAppData.name === 'string' && mobileAppData.name.trim()
  );
  const mobileAppName = hasMobileAppName ? mobileAppData.name.trim() : 'Non renseigné';
  const parseRatingValue = ratingValue => {
    if (typeof ratingValue === 'number') {
      return ratingValue;
    }
    if (typeof ratingValue === 'string') {
      const normalized = ratingValue.trim().replace(',', '.');
      if (!normalized) {
        return NaN;
      }
      return Number(normalized);
    }
    return NaN;
  };
  const playStoreRatingRaw = parseRatingValue(mobileAppData?.rating_on_playstore);
  const appStoreRatingRaw = parseRatingValue(mobileAppData?.rating_on_applestore);
  const playStoreRating = Number.isFinite(playStoreRatingRaw)
    ? Math.max(0, Math.min(5, Math.round(playStoreRatingRaw)))
    : null;
  const appStoreRating = Number.isFinite(appStoreRatingRaw)
    ? Math.max(0, Math.min(5, Math.round(appStoreRatingRaw)))
    : null;
  const ratingDate =
    mobileAppData &&
    typeof mobileAppData.rating_date === 'string' &&
    mobileAppData.rating_date.trim()
      ? mobileAppData.rating_date.trim()
      : null;
  const playStoreLink =
    mobileAppData &&
    typeof mobileAppData.link_to_playstore === 'string' &&
    mobileAppData.link_to_playstore.trim()
      ? mobileAppData.link_to_playstore.trim()
      : null;
  const appStoreLink =
    mobileAppData &&
    typeof mobileAppData.link_to_apple_store === 'string' &&
    mobileAppData.link_to_apple_store.trim()
      ? mobileAppData.link_to_apple_store.trim()
      : null;
  const playStoreRatingComment =
    mobileAppData &&
    typeof mobileAppData.rating_on_playstore_comment === 'string' &&
    mobileAppData.rating_on_playstore_comment.trim()
      ? mobileAppData.rating_on_playstore_comment.trim()
      : null;
  const appStoreRatingComment =
    mobileAppData &&
    typeof mobileAppData.rating_on_applestore_comment === 'string' &&
    mobileAppData.rating_on_applestore_comment.trim()
      ? mobileAppData.rating_on_applestore_comment.trim()
      : null;
  const renderStarRating = rating =>
    rating == null ? null : `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}`;

  // Extract product features from public data
  const productFeatures = [
    publicData.short_feature_1,
    publicData.short_feature_2,
    publicData.short_feature_3,
    publicData.short_feature_4,
    publicData.short_feature_5,
  ].filter(feature => feature && feature.trim() !== '');

  /**
   * Slug deviceType cohérent avec les libellés / clés enum (espaces, tirets, snake_case, camelCase).
   */
  const normalizeDeviceTypeValue = value => {
    if (!value) return '';
    let raw = Array.isArray(value) ? `${value[0]}` : `${value}`;
    raw = raw.replace(/\u00a0/g, ' ').trim();
    if (!raw) return '';
    raw = raw
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
    raw = raw
      .replace(/-+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return createSlug(raw);
  };

  /** Fiches « robot piscine » / Avis Vérifiés SAV : couvre Robot nettoyeur de piscine et variantes slug. */
  const normalizedSlugLooksLikePoolCleaningRobot = slug => {
    if (!slug || slug === 'no-slug') return false;
    const s = String(slug).toLowerCase();
    if (s.includes('pool-clean') || s.includes('robotic-pool')) return true;
    if (!s.includes('piscine')) return false;
    return (
      s.includes('nettoyeur') ||
      s.includes('nettoyage') ||
      s.includes('robot') ||
      s.includes('pool')
    );
  };

  const rawDeviceType = publicData.deviceType || publicData.device_type;
  const normalizedDeviceType = normalizeDeviceTypeValue(rawDeviceType);
  const isRobotAfterSalesDeviceType =
    [
      'robot-tondeuse',
      'robots-tondeuses',
      'robot-tondeuses',
      'tondeuse-robot',
      'tondeuses-robots',
      'lawn-mower',
      'lawn-mowers',
      'robot-nettoyeur-de-piscine',
      'robots-nettoyeurs-de-piscine',
      'robot-nettoyeur-piscine',
      'robots-nettoyeurs-piscine',
      'robots-nettoyeur-de-piscine',
      'robot-nettoyeurs-de-piscine',
      'robot-de-piscine',
      'robots-de-piscine',
      'robot-piscine',
      'nettoyeur-de-piscine',
      'pool-cleaner',
      'pool-cleaners',
      'robotic-pool-cleaner',
      'pool-cleaning-robot',
      'robot-de-nettoyage-de-piscine',
      'robot-pool-cleaner',
    ].includes(normalizedDeviceType) ||
    normalizedSlugLooksLikePoolCleaningRobot(normalizedDeviceType) ||
    ['lawn-mowers', 'robots-de-piscine'].includes(publicData.categoryLevel1);
  const compareDeviceTypeMap = {
    'camera-de-securite': 'camera-de-securite',
    'camera-securite': 'camera-de-securite',
    thermostat: 'thermostat',
    'thermostat-connecte': 'thermostat',
    'prise-connectee': 'prise-connectee',
    'serrure-connectee': 'serrure-connectee',
  };
  const compareDeviceTypeId = compareDeviceTypeMap[normalizedDeviceType];
  const isCompareDeviceType = !!compareDeviceTypeId;
  const compareListingId = currentListing?.id?.uuid;
  const compareDeviceTypeLabel =
    compareDeviceTypeId === 'thermostat'
      ? 'thermostat'
      : compareDeviceTypeId === 'prise-connectee'
      ? 'prise connectée'
      : compareDeviceTypeId === 'serrure-connectee'
      ? 'serrure connectée'
      : 'caméra';

  const brandDisplayName = getBrandDisplayName(brand);

  const inTheBoxItems = parseStringifiedStringList(
    publicData.inthebox ?? publicData.in_the_box ?? publicData.inTheBox
  );
  const warrantyItems = parseStringifiedStringList(publicData.warranty, { splitOnComma: false });
  const documents = parseDocumentsList(publicData.documents);
  // Toujours afficher la section : garantie légale par défaut dans le bloc Garanties + bloc Documents.
  const hasExtraProductInfoSection = true;

  const { listingType, transactionProcessAlias, unitType } = publicData;

  // Validate required publicData fields after extraction
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType.
    // If we are still fetching the full listing, show the spinner to avoid a red
    // error flash while partial/cached data (e.g. from SearchPage) is displayed.
    if (showListingInProgress) {
      return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
    }
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }

  // Avis marketplace + widget Avis Verifies/Skeepers (si SKU disponible).
  const userReviewsCount = Array.isArray(reviews) ? reviews.length : 0;
  const hasUserReviews = userReviewsCount > 0;
  const skeepersProductIds = normalizeSkeepersProductIds(publicData.sku);
  const hasSkeepersProductReviews = skeepersProductIds.length > 0;
  const showReviewsSection =
    hasUserReviews ||
    !!fetchReviewsError ||
    (hasSkeepersProductReviews && skeepersWidgetStatus !== 'empty');
  const showUserReviewsSubsection = hasUserReviews || !!fetchReviewsError;
  const hideReviewsSectionWhileSkeepersPending =
    hasSkeepersProductReviews && !showUserReviewsSubsection && skeepersWidgetStatus === null;

  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmitCheckout = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const onSubmitAddToCart = values => {
    const orderData = values || {};
    const applyPromoPromise = isRoboupPromoEligibleAfterSignup
      ? props.onSetCartPromoCode(ROBOUP_PROMO_CODE)
      : isReolinkPromoEligibleAfterSignup
      ? props.onSetCartPromoCode(REOLINK_PROMO_CODE)
      : Promise.resolve();
    return applyPromoPromise
      .then(() => props.onAddToCart(currentListing, orderData))
      .then(() => {
        // Show success toast instead of immediate redirect
        setCartPanelOpen(true);
      })
      .catch(() => {});
  };

  const handleAddBundleToCart = async bundle => {
    if (!bundle || !Array.isArray(bundle.products) || bundle.products.length === 0) return;
    setAddingBundleId(bundle.id);
    try {
      for (const product of bundle.products) {
        const productListing = bundleProductListingsById?.[product.id];
        if (!productListing) continue;
        const orderData = buildCartOrderDataForListing(
          productListing,
          Number(product.quantity) > 0 ? Number(product.quantity) : 1
        );
        // eslint-disable-next-line no-await-in-loop
        await props.onAddToCart(productListing, orderData);
      }
      const bundlePromoCode = bundle?.price?.promo_code;
      if (bundlePromoCode && props.onAddCartPromoCode) {
        await props.onAddCartPromoCode(bundlePromoCode);
        await props.onAddBundleCartPromoCode(bundlePromoCode);
      }
      setCartPanelOpen(true);
    } catch (e) {
      // Swallow – surface via toast/analytics later.
    } finally {
      setAddingBundleId(null);
    }
  };

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmitCheckout(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const skeepersPriceBits = priceForSchemaMaybe(price);
  const skeepersPriceForWidget = skeepersPriceBits.price || '';
  const skeepersCurrencyForWidget = skeepersPriceBits.priceCurrency || config.currency || '';
  const skeepersImageUrlForWidget = schemaImages[0] || '';
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  const mobileCompareCta =
    isCompareDeviceType && compareListingId ? (
      <div className={css.compareMobileCtaWrapper}>
        <NamedLink
          name="ComparePage"
          params={{ device_type: compareDeviceTypeId }}
          to={{ search: `?compare=${compareListingId}` }}
          className={css.compareMobileCta}
        >
          <svg
            className={css.compareBreadcrumbIcon}
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M10.08,7l1,1,3.44-3.45L11,1,10,2l1.8,1.8H2v1.4h9.82ZM5.86,9l-1-1L1.42,11.5,4.91,15l1-1L4.1,12.2H14V10.8H4.1Z" />
          </svg>
          Comparer avec un autre produit
        </NamedLink>
      </div>
    ) : null;

  return (
    <SkeepersAvisVerifiesListingContext.Provider
      value={{ mountWidgetInListingAfterSales: isRobotAfterSalesDeviceType }}
    >
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        author={authorDisplayName}
        description={description}
        facebookImages={facebookImages}
        twitterImages={twitterImages}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'Product',
          description: description,
          name: schemaTitle,
          image: schemaImages,
          offers: {
            '@type': 'Offer',
            url: productURL,
            ...priceForSchemaMaybe(price),
            ...availabilityMaybe,
          },
        }}
      >
        <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
          <div className={css.breadcrumbRow}>
            <ProductBreadcrumb publicData={publicData} productTitle={title} />
            {isCompareDeviceType && compareListingId ? (
              <NamedLink
                name="ComparePage"
                params={{ device_type: compareDeviceTypeId }}
                to={{ search: `?compare=${compareListingId}` }}
                className={css.compareBreadcrumbCta}
              >
                <svg
                  className={css.compareBreadcrumbIcon}
                  viewBox="0 0 16 16"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M10.08,7l1,1,3.44-3.45L11,1,10,2l1.8,1.8H2v1.4h9.82ZM5.86,9l-1-1L1.42,11.5,4.91,15l1-1L4.1,12.2H14V10.8H4.1Z" />
                </svg>
                Comparer
              </NamedLink>
            ) : null}
          </div>

          <div className={css.contentWrapperForProductLayout}>
            <div className={css.imageColumnForProductLayout}>
              {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                  currentUser={currentUser}
                />
              ) : null}
              {mounted && currentListing.id ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  currentUser={currentUser}
                  editParams={{
                    id: listingId.uuid,
                    slug: listingSlug,
                    type: listingPathParamType,
                    tab: listingTab,
                  }}
                />
              ) : null}
              {showListingImage && (
                <SectionGallery
                  listing={currentListing}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                />
              )}

              {/* Temporarily hidden on desktop; badges will be moved elsewhere */}
              <div className={css.compatibilityBadgesDesktopHidden}>
                <CompatibilityBadges compatibility={compatibility} config={config} />
              </div>
            </div>

            <div className={css.middleColumnForProductLayout}>
              <div className={css.productInfo}>
                <div className={css.productHeader}>
                  {brandDisplayName && <div className={css.brandInfo}>{brandDisplayName}</div>}
                  <h3 className={css.productTitle}>{richTitle}</h3>
                </div>
                {shortDescription && (
                  <p className={css.productShortDescription}>{shortDescription}</p>
                )}
                {productFeatures.length > 0 && (
                  <ul className={css.productFeatures}>
                    {productFeatures.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                )}

                <VariantSelector
                  variantsRaw={publicData?.variants}
                  currentListingId={currentListing?.id?.uuid}
                  mode="mobile"
                />

                {/* Sold by vendor - visible on mobile only */}
                <p className={css.mobileSoldByVendor}>
                  Vendu par{' '}
                  <button
                    type="button"
                    className={classNames(css.authorNameLink, css.authorNameButton)}
                    onClick={openSellerPanel}
                  >
                    {authorDisplayName}
                  </button>
                </p>

                {/* Delivery information - visible on mobile only */}
                <SectionDeliveryInfo
                  listing={currentListing}
                  price={price}
                  intl={intl}
                  onOpenLoyaltyPanel={openLoyaltyPanel}
                />

                {/* Manufacturer reference - discreet display in bottom right */}
                {publicData.ref_manufacturer && (
                  <div className={css.manufacturerRef}>
                    Réf. fabricant: {publicData.ref_manufacturer}
                  </div>
                )}
              </div>
            </div>

            <div className={css.orderColumnForProductLayout}>
              <div
                className={showListingImage ? css.mobileHeading : css.noListingImageHeadingProduct}
              >
                {showListingImage ? (
                  <H4 as="h2" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H4>
                ) : (
                  <H3 as="h2" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H3>
                )}
              </div>
              <OrderPanel
                className={classNames(css.productOrderPanel, {
                  [css.imagesEnabled]: showListingImage,
                })}
                listing={listingForOrderPanel}
                isOwnListing={isOwnListing}
                onSubmit={isPurchase ? onSubmitAddToCart : handleOrderSubmit}
                authorLink={
                  <NamedLink
                    className={css.authorNameLink}
                    name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                    params={params}
                    to={{ hash: '#author' }}
                  >
                    {authorDisplayName}
                  </NamedLink>
                }
                title={
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                }
                titleDesktop={
                  <H4 as="h2" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H4>
                }
                payoutDetailsWarning={payoutDetailsWarning}
                author={ensuredAuthor}
                onManageDisableScrolling={onManageDisableScrolling}
                onContactUser={onContactUser}
                onOpenSellerPanel={openSellerPanel}
                onOpenLoyaltyPanel={openLoyaltyPanel}
                reviews={reviews}
                fetchReviewsError={fetchReviewsError}
                {...restOfProps}
                validListingTypes={config.listing.listingTypes}
                marketplaceCurrency={config.currency}
                dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                marketplaceName={config.marketplaceName}
                showListingImage={showListingImage}
                campaignPromoStrikeThroughPrice={campaignPromoStrikeThroughPrice}
                campaignPromoPercentOff={5}
              />
            </div>
          </div>

          {/* Similar Products Section */}
          {similarListings.length > 0 && (
            <SimilarListingsSection
              listings={similarListings}
              intl={intl}
              mobileCompareCta={mobileCompareCta}
            />
          )}

          {/* Tabs Section */}
          <div className={css.tabsSection}>
            <h2 className={css.productInfoHeading}>Information Produit</h2>
            <Tabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              publicData={publicData}
              listingTitle={title}
            />
          </div>

          {bundles && bundles.length > 0 ? (
            <SectionBundles
              bundles={bundles}
              productListingsById={bundleProductListingsById}
              onAddBundleToCart={handleAddBundleToCart}
              intl={intl}
              config={config}
              addingBundleId={addingBundleId}
            />
          ) : null}

          {isRobotAfterSalesDeviceType ? <NeokasaAfterSalesSection /> : null}

          <section className={css.smartAdvantageSection}>
            <h2 className={css.smartAdvantageSectionHeading}>
              Un atout pour votre maison connectée
            </h2>
            <div
              className={classNames(css.smartAdvantageGrid, {
                [css.smartAdvantageGridNoMain]: !hasSmartAdvantageContent,
              })}
            >
              {hasSmartAdvantageContent ? (
                <div className={css.smartAdvantageMainColumn}>
                  {smartAdvantageTitle ? (
                    <h3 className={css.smartAdvantageTitle}>{smartAdvantageTitle}</h3>
                  ) : null}
                  {smartAdvantageUseCases.length > 0 ? (
                    <p className={css.smartAdvantageIntro}>
                      Ce produit vous permettra notamment de :
                    </p>
                  ) : null}
                  {smartAdvantageUseCases.length > 0 ? (
                    <ul className={css.smartAdvantageUseCasesList}>
                      {smartAdvantageUseCases.map((useCase, index) => (
                        <li key={`${useCase}-${index}`} className={css.smartAdvantageUseCaseItem}>
                          {useCase}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              <aside
                className={classNames(css.smartAdvantageSideColumn, {
                  [css.smartAdvantageSideColumnOnly]: !hasSmartAdvantageContent,
                  [css.smartAdvantageSideColumnTwo]:
                    !hasSmartAdvantageContent && !hasCompatibilityBlockContent,
                })}
              >
                {hasCompatibilityBlockContent ? (
                  <div className={css.smartAdvantageInfoBlock}>
                    {hasCompatibilityData ? (
                      <>
                        <h3 className={css.smartAdvantageInfoTitle}>
                          Compatibilité avec les écosystèmes domotiques
                        </h3>
                        <CompatibilityBadges compatibility={compatibility} config={config} />
                      </>
                    ) : null}
                    {homeAssistantIntegrationLink ? (
                      <button
                        onClick={e => {
                          e.preventDefault();
                          openSidePanel(homeAssistantIntegrationLink);
                        }}
                        className={classNames(
                          css.homeAssistantIntegrationLink,
                          css.smartAdvantageIntegrationButton
                        )}
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/a/ab/New_Home_Assistant_logo.svg"
                          alt="Home Assistant"
                          className={css.integrationLogo}
                        />
                        Intégration Home Assistant
                        <svg
                          className={css.externalLinkIcon}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        >
                          <path d="M7 17L17 7M17 7H7M17 7v10" />
                        </svg>
                      </button>
                    ) : null}
                    {zigbee2mqttLink ? (
                      <button
                        onClick={e => {
                          e.preventDefault();
                          openSidePanel(zigbee2mqttLink);
                        }}
                        className={classNames(
                          css.homeAssistantIntegrationLink,
                          css.smartAdvantageIntegrationButton
                        )}
                      >
                        <img
                          src="https://www.zigbee2mqtt.io/logo.png"
                          alt="Zigbee2mqtt"
                          className={css.integrationLogo}
                        />
                        Intégration Zigbee2MQTT
                        <svg
                          className={css.externalLinkIcon}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        >
                          <path d="M7 17L17 7M17 7H7M17 7v10" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                ) : null}

                <div className={css.smartAdvantageInfoBlock}>
                  <h3 className={css.smartAdvantageInfoTitle}>Connectivité</h3>
                  <SectionConnectivityGrid connectivity={connectivity} config={config} />
                </div>

                <div className={css.smartAdvantageInfoBlock}>
                  <h3 className={css.smartAdvantageInfoTitle}>Application mobile</h3>
                  <p
                    className={classNames(css.smartAdvantageMobileAppName, {
                      [css.smartAdvantageMobileAppNameUnset]: !hasMobileAppName,
                    })}
                  >
                    {mobileAppName}
                  </p>
                  {playStoreRating != null || playStoreRatingComment ? (
                    <p
                      className={classNames(
                        css.smartAdvantageInfoValue,
                        css.smartAdvantageRatingLine
                      )}
                    >
                      <span className={css.smartAdvantageRatingRow}>
                        <span className={css.smartAdvantageRatingStoreLabel}>Play Store:</span>
                        {playStoreRating != null ? (
                          <span className={css.smartAdvantageStarRating}>
                            {renderStarRating(playStoreRating)}
                          </span>
                        ) : null}
                        {playStoreRatingComment ? (
                          <span className={css.smartAdvantageRatingComment}>
                            {playStoreRatingComment}
                          </span>
                        ) : null}
                      </span>
                    </p>
                  ) : null}
                  {appStoreRating != null || appStoreRatingComment ? (
                    <p
                      className={classNames(
                        css.smartAdvantageInfoValue,
                        css.smartAdvantageRatingLine
                      )}
                    >
                      <span className={css.smartAdvantageRatingRow}>
                        <span className={css.smartAdvantageRatingStoreLabel}>App Store:</span>
                        {appStoreRating != null ? (
                          <span className={css.smartAdvantageStarRating}>
                            {renderStarRating(appStoreRating)}
                          </span>
                        ) : null}
                        {appStoreRatingComment ? (
                          <span className={css.smartAdvantageRatingComment}>
                            {appStoreRatingComment}
                          </span>
                        ) : null}
                      </span>
                    </p>
                  ) : null}
                  {ratingDate ? (
                    <p className={css.smartAdvantageRatingDate}>Notes en {ratingDate}</p>
                  ) : null}
                  {playStoreLink || appStoreLink ? (
                    <div className={css.smartAdvantageStoreLinks}>
                      {playStoreLink ? (
                        <a
                          href={playStoreLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={css.smartAdvantageStoreLink}
                        >
                          <img
                            src={playStoreIcon}
                            alt="Google Play"
                            className={css.smartAdvantageStoreBadge}
                          />
                        </a>
                      ) : null}
                      {appStoreLink ? (
                        <a
                          href={appStoreLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={css.smartAdvantageStoreLink}
                        >
                          <img
                            src={appStoreIcon}
                            alt="App Store"
                            className={css.smartAdvantageStoreBadge}
                          />
                        </a>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </aside>
            </div>
          </section>

          {/* YouTube Videos Section Container */}
          {youtubeVideoIds.length > 0 && (
            <div className={css.youtubeSectionContainer}>
              <YouTubeVideosSection productName={title} videoIds={youtubeVideoIds} />
            </div>
          )}

          {/* Brand Products Section */}
          {brandListings.length > 0 && (
            <BrandListingsSection
              listings={brandListings}
              brandName={brandDisplayName}
              intl={intl}
            />
          )}

          {hasExtraProductInfoSection ? (
            <ProductBoxWarrantyDocumentsSection
              inTheBox={inTheBoxItems}
              warranty={warrantyItems}
              documents={documents}
            />
          ) : null}

          {/* FAQ Section Container */}
          {askedQuestions.length > 0 && (
            <div className={css.faqSectionContainer}>
              <FAQSection productName={title} faqs={askedQuestions} />
            </div>
          )}

          {/* Reviews Section — avis marketplace (transactions) */}
          {showReviewsSection ? (
            <div
              id="reviews-section"
              className={classNames(css.reviewsSectionContainer, {
                [css.reviewsSectionContainerPending]: hideReviewsSectionWhileSkeepersPending,
              })}
            >
              <div className={css.reviewsSectionInner}>
                <h2 className={css.reviewsSectionHeading}>Avis</h2>

                {showUserReviewsSubsection ? (
                  <>
                    <h3 className={css.userReviewsTitle}>Avis clients ({reviews.length})</h3>
                    {fetchReviewsError ? (
                      <div className={css.errorText}>
                        <p>Échec du chargement des avis. Veuillez réessayer plus tard.</p>
                      </div>
                    ) : (
                      <div className={css.reviewsContainer}>
                        {reviews.map(review => (
                          <div key={review.id.uuid} className={css.reviewItem}>
                            <div className={css.reviewHeader}>
                              <div className={css.reviewStars}>
                                {'★'.repeat(review.attributes.rating || 5)}
                                {'☆'.repeat(5 - (review.attributes.rating || 5))}
                              </div>
                              <div className={css.reviewAuthor}>
                                {review.author?.attributes?.profile?.displayName || 'Anonyme'}
                              </div>
                              <div className={css.reviewDate}>
                                {new Date(review.attributes.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                            <p>"{review.attributes.content}"</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : null}

                {hasSkeepersProductReviews ? (
                  <SkeepersProductReviews
                    key={currentListing.id.uuid}
                    productIds={skeepersProductIds}
                    productTitle={title}
                    productPrice={skeepersPriceForWidget}
                    currency={skeepersCurrencyForWidget}
                    productUrl={productURL}
                    imageUrl={skeepersImageUrlForWidget}
                    locale="fr_FR"
                    sectionClassName={css.skeepersReviewsBlock}
                    headingClassName={css.userReviewsTitle}
                    onResolve={handleSkeepersProductResolve}
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          <SkeepersCarouselReviews title="Nos clients parlent de nous" />
          <TrustBanner />

          {/* Full-width description and details section */}
          {/* <div className={css.fullWidthDescriptionContainer}>
          <SectionDescriptionAndDetails
            text={description}
            showAsIngress
            enableMarkdown
            publicData={publicData}
            metadata={metadata}
            listingFieldConfigs={listingConfig.listingFields}
            categoryConfiguration={config.categoryConfiguration}
            intl={intl}
          />
        </div> */}

          {geolocation && (
            <div className={css.contentWrapperForProductLayout}>
              <div className={css.mainColumnForProductLayout}>
                <SectionMapMaybe
                  geolocation={geolocation}
                  publicData={publicData}
                  listingId={currentListing.id}
                  mapsConfig={config.maps}
                />
              </div>
            </div>
          )}
        </LayoutSingleColumn>

        {/* Side Panel with iframe for integration links */}
        {sidePanelOpen && (
          <>
            <div className={css.sidePanelOverlay} onClick={() => setSidePanelOpen(false)} />
            <div className={css.sidePanel}>
              <div className={css.sidePanelHeader}>
                <div className={css.sidePanelHeaderContent}>
                  <div className={css.sidePanelTitleRow}>
                    <h3 className={css.sidePanelTitle}>Documentation externe</h3>
                    {/* <span className={css.sidePanelSeparator}>•</span> */}
                    <div className={css.sidePanelSource}>
                      Source : {new URL(sidePanelUrl).hostname}
                    </div>
                  </div>
                  <div className={css.sidePanelLegalNotice}>
                    Les informations ci-dessous sont fournies par un tiers à titre informatif.
                  </div>
                </div>
                <button
                  className={css.sidePanelClose}
                  onClick={() => setSidePanelOpen(false)}
                  aria-label="Fermer"
                >
                  ✕
                </button>
              </div>
              <div className={css.sidePanelContent}>
                {iframeLoading && (
                  <div className={css.sidePanelLoading}>
                    <div className={css.spinner}></div>
                    <p>Chargement...</p>
                  </div>
                )}
                <iframe
                  src={sidePanelUrl}
                  className={css.sidePanelIframe}
                  title="Integration Documentation"
                  frameBorder="0"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                  referrerPolicy="no-referrer"
                  loading="lazy"
                  onLoad={() => setIframeLoading(false)}
                />
              </div>
            </div>
          </>
        )}

        <SellerInfoPanel isOpen={sellerPanelOpen} onClose={closeSellerPanel} author={ensuredAuthor} />
        <LoyaltyProgramPanel isOpen={loyaltyPanelOpen} onClose={closeLoyaltyPanel} />

        <CartSidePanel open={cartPanelOpen} onClose={() => setCartPanelOpen(false)} />
      </Page>
    </SkeepersAvisVerifiesListingContext.Provider>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    showListingInProgress,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    similarListingIds,
    brandListingIds,
    bundles = [],
    bundleProductIds = [],
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const similarListingRefs = (similarListingIds || []).map(id => ({ id, type: 'listing' }));
  const similarListings = getMarketplaceEntities(state, similarListingRefs);

  const brandListingRefs = (brandListingIds || []).map(id => ({ id, type: 'listing' }));
  const brandListings = getMarketplaceEntities(state, brandListingRefs);

  const bundleProductRefs = (bundleProductIds || []).map(id => ({ id, type: 'listing' }));
  const bundleProductListings = getMarketplaceEntities(state, bundleProductRefs);
  const bundleProductListingsById = bundleProductListings.reduce((acc, listing) => {
    const id = listing?.id?.uuid || listing?.id;
    if (id) acc[id] = listing;
    return acc;
  }, {});

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    showListingInProgress,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    similarListings,
    brandListings,
    bundles,
    bundleProductListingsById,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  onAddToCart: (listing, orderData) => dispatch(addToCart({ listing, orderData })),
  onSetCartPromoCode: code => dispatch(setCartPromoCode(code)),
  onAddCartPromoCode: code => dispatch(addCartPromoCode(code)),
  onAddBundleCartPromoCode: code => dispatch(addBundleCartPromoCode(code)),
  onFetchSimilarListings: (listingId, categoryLevel1, config) =>
    dispatch(fetchSimilarListings(listingId, categoryLevel1, config)),
  onFetchBrandListings: (listingId, brand, config) =>
    dispatch(fetchBrandListings(listingId, brand, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
